<template>
  <div class="ticket-create-view">
    <GrootHeroBanner
      :title="$t('ticket-create.heading')"
      :preamble="$t('ticket-create.preamble')"
      :breadcrumbs="showBreadcrumbs ? breadcrumbItems : []" />

    <CreateTicketContainer
      :language="locale"
      :caseTypes="caseTypes"
      :billingCaseTypes="billingCaseTypes"
      :products="products"
      :loading="loading"
      :exception="exception"
      :goBack="goBack"
      :submit="submit" />
  </div>
</template>

<script>
import { BREADCRUMBS_CUSTOMER_TICKETS } from '@/constants/breadcrumbs-constants'
import { applyReactInVueWithOptions } from '@/utils/react.utils'
import {
  GrootCreateTicketContainer, GrootHeroBanner
} from 'groot-components'
import Constants from '../../utils/constants'
import {
  buildBillingTicketRequest,
  buildTicketCreateRequest
} from '../../utils/request.utils'

export default {
  components: {
    CreateTicketContainer: applyReactInVueWithOptions(
      GrootCreateTicketContainer
    ),
    GrootHeroBanner: applyReactInVueWithOptions(GrootHeroBanner)
  },
  props: {
    showBreadcrumbs: {
      type: Boolean,
      required: false,
      default: true
    },
    showBackButton: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      locale: this.$route.params?.locale,
      breadcrumbItems: BREADCRUMBS_CUSTOMER_TICKETS.CREATE,
      formFieldLabels: {
        caseType: this.$t('ticket-create.case-type'),
        customerReference: this.$t('ticket-create.customer-reference'),
        product: this.$t('ticket-create.product'),
        subject: this.$t('ticket-create.subject'),
        content: this.$t('ticket-create.content-label'),
        orderId: this.$t('ticket-create.field-labels.order-number'),
        assetId: this.$t('ticket-create.field-labels.asset-number'),
        ttId: this.$t('ticket-create.field-labels.complaint-number'),
        receiveEmailNotification: this.$t(
          'ticket-create.field-labels.email-notification'
        ),
        responseEmail: this.$t('ticket-create.field-labels.response-email')
      },
      validationMessages: {
        caseType: {
          required: this.$t('validators.ticket-create.required')
        },
        customerReference: {
          maxLength: this.$t('validators.ticket-create.customer-reference')
        },
        product: {
          required: this.$t('validators.ticket-create.required')
        },
        subject: {
          required: this.$t('validators.ticket-create.required')
        },
        content: {
          required: this.$t('validators.ticket-create.required')
        },
        orderId: {
          orderId: this.$t('validators.ticket-create.order-id')
        },
        assetId: {
          assetId: this.$t('validators.ticket-create.asset-id')
        },
        ttId: {
          ttId: this.$t('validators.ticket-create.tt-id')
        },
        responseEmail: {
          required: this.$t('validators.ticket-create.required'),
          email: this.$t('validators.ticket-create.email')
        }
      },
      referenceText: this.$t('ticket-create.reference'),
      addFileText: this.$t('ticket-create.attachments.button'),
      addFileHeading: this.$t('ticket-create.attachments.heading'),
      submitButtonText: this.$t('ticket-create.buttons.create'),
      goBackButtonText: this.$t('ticket-create.buttons.go-back')
    }
  },
  computed: {
    response () {
      return this.$store.getters['CustomerTickets/TicketCreate/response']
    },
    loading () {
      return this.$store.getters['CustomerTickets/TicketCreate/loading']
    },
    exception () {
      return this.$store.getters['CustomerTickets/TicketCreate/exception']
    },
    products () {
      const productCategories = Constants.CATEGORIZED_PRODUCTS

      return Object.keys(productCategories).map((categoryKey) => {
        const options = productCategories[categoryKey].map((productKey) => {
          return {
            label: this.$t(productKey),
            value: productKey
          }
        })

        return {
          label: this.$t(categoryKey),
          options: options
        }
      })
    },
    caseTypes () {
      return Constants.CASE_TYPES_OPTIONS.map((option) => {
        const value = option.split('.').pop()
        return {
          title: this.$t(`constants.case-types.${value}`),
          value: option
        }
      })
    },
    billingCaseTypes () {
      return Constants.BILLING_CASE_TYPES_OPTIONS.map((option) => {
        const value = option.split('.').pop()
        return {
          title: this.$t(`constants.billing-case-types.${value}`),
          value: option
        }
      })
    }
  },
  watch: {
    response (newVal) {
      if (this.exception) {
        return
      }

      this.$emit('goToDetails', {
        name: 'tickets-details',
        params: { supportId: newVal.supportId || newVal.id },
        query: { created: true }
      })
    }
  },
  methods: {
    goBack () {
      this.$emit('goBack', { name: 'tickets-list' })
    },
    async submit (form) {
      const isBilling = form.caseType === 'constants.case-types.billing'
      const payload = {
        ticketCreateRequest: isBilling ? buildBillingTicketRequest(form) : buildTicketCreateRequest(form)
      }

      if (!isBilling) {
        payload.files = form.files.map((file) => ({
          file,
          fileName: file.name,
          fileKey: file.name
        }))
      }

      const action = isBilling
        ? 'CustomerTickets/TicketCreate/postRequestBilling'
        : 'CustomerTickets/TicketCreate/postRequest'

      await this.$store.dispatch(action, payload)
    }
  }

}
</script>
<style lang="scss">
@import "groot-components/dist/style.css";
.document {
  --purpur-color-border-weak: rgba(0, 0, 0, 0.16);
}
</style>
